import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "../../axios/axiosInstance";
import { useAuth } from "../../Context/AuthContext";
import { isValidArray, scrollToTop } from "../../Layout/Common/common";
import { useLocation } from "react-router-dom";
import { ProductGallerySkeleton } from "../../Skeleton/productGallerySkeleton";
import { loadImageouchScriptNew } from "./scripts";
import { ProductDetailsContainer } from "./productDetailsContainer";
import { useGlobalFunction } from "../../Context/GlobalFunctionContext";
import { HybridDownloadContainer } from "./downloadContainer/hybridDownloadContainer";
import { AskQuestionContainer } from "./askQuestionContainer";
import { HtmlFeatureBlogContainer } from "./htmlFeatureBlogContainer";
import { HybridpageRightLoader } from "../../Skeleton/hybridpageRightLoader";
import { BreadcrumbSkeleton } from "../../Skeleton/breadCrumbSkeleton";
import { SuggestionProductSkeleton } from "../../Skeleton/suggestionProductSkeleton";
import { PopularProductContainer } from "./popularProductContainer";
import { FrequentlyBoughtTogether } from "./frequentlyBoughtTogether";
import { RelatedProductsContainer } from "./relatedProductsContainer";
import { HybridBreadCrumb } from "../../Layout/Common/hybridBreadCrumb";
import { HybridProductContainer } from "./family/hybridProductContainer";
import { useFlashLoaderLoader } from "../../Context/FlashScreenContext";
import { GlobalContext } from "../../Context/GlobalContext";
import { SuggestionProducts } from "./suggestionProducts";
import { usePageTitle } from "../../utils/pageTitle";
import { ProductCardPopularProduct } from "../../Layout/Common/productCardPopularProduct";
import { ProductOverlay } from "./productOverlay/productOverlay";
import { ProductOverlayContext } from "./productOverlay/productOverlayContext";
import { OVERLAY_EXIT_MS, sameLayerState, useProductPageOverlay } from "./productPageOverlay/productPageOverlayContext";
import { useExitPresence } from "../../utils/useExitPresence";
import { findProductBySku, getFamilyPage, getFamilyProducts } from "./productApi";
import { getProductPreview } from "./productPreviewCache";

const OVERLAY_STATE_KEY = "productOverlay";

const toBreadcrumb = (items) =>
  isValidArray(items) ? items.map((item) => ({ name: item.itemValue ?? item.ItemValue, url: item.url ?? item.Url })) : null;

const skuFromSearch = (search) => {
  const skuMatch = String(search ?? "").match(/[?&]sku=([^&]*)/);
  return skuMatch ? decodeURIComponent(skuMatch[1]) : "";
};

export const ProductHybridMain = () => {
  const location = useLocation();
  const [pageData, setPageData] = useState(() => getProductPreview(location.pathname, location.search));
  const [breadCrumb, setBreadcrumb] = useState(() => toBreadcrumb(pageData?.breadCrumb));
  const [familyid, setFamilyId] = useState(null);
  const [ishybrid, setIshybrid] = useState(false);
  const [hybridFilter, setHybridFilter] = useState(null);
  const [productStockStatus, setProductStockStatus] = useState(null);
  const { getStockStatus } = useGlobalFunction();
  const { categoryDataAll } = useContext(GlobalContext);
  const { showLoader, hideLoader } = useFlashLoaderLoader();
  const [hybridFamilyProducts, setHybridFamilyProducts] = useState(null);
  const { fid } = useParams();
  const { userId, priceCode } = useAuth();
  const navigate = useNavigate();
  const pageOverlay = useProductPageOverlay();
  const prevValue = useRef();
  const familyRef = useRef();
  const familyProductsRef = useRef({ familyId: null, products: null });
  const loadedFidRef = useRef(null);
  const requestRef = useRef(0);
  const stockProductRef = useRef(null);
  const searchParams = new URLSearchParams(location.search);
  const state = location.state;
  const loadedHrefRef = useRef(null);
  const overlayOpenedHereRef = useRef(false);
  const skuParam = searchParams.get("sku");
  const familySearchParams = new URLSearchParams(location.search);
  familySearchParams.delete("sku");
  const familySearch = familySearchParams.toString() ? `?${familySearchParams.toString()}` : "";
  const familyHref = location.pathname + familySearch;
  const overlaySku =
    state?.[OVERLAY_STATE_KEY] === true &&
    skuParam &&
    loadedHrefRef.current === familyHref &&
    pageData?.familyDetails?.isFamily == true
      ? skuParam
      : null;
  const pageHref = overlaySku ? familyHref : location.pathname + location.search;
  const familyName = pageData?.familyDetails?.familyName;
  usePageTitle(pageOverlay ? null : familyName);
  const setOverlayTitle = pageOverlay?.setTitle;
  useEffect(() => {
    if (setOverlayTitle && familyName) setOverlayTitle(familyName);
  }, [setOverlayTitle, familyName]);

  const loadInitialData = async () => {
    const request = ++requestRef.current;
    const data = {
      familyId: fid,
      userId: String(userId ?? 999),
      sku: skuFromSearch(location.search),
      priceCode: String(priceCode ?? 3),
    };
    const preview = getProductPreview(location.pathname, location.search);
    if (preview) applyPageData(preview);
    else if (pageOverlay?.isTop !== false) showLoader();
    setIshybrid(false);
    if (data.sku) getProductHybrid(data, request);
    else setHybridFilter(null);

    const cachedProduct = findProductBySku(getCachedFamilyProducts(), data.sku);
    if (cachedProduct) {
      applyPageData(cachedProduct);
      return;
    }

    const loadedFamilyId =
      loadedFidRef.current == fid && !pageData?.isPreview ? pageData?.familyDetails?.familyId : null;
    if (data.sku && loadedFamilyId && !getCachedFamilyProducts()) {
      const restored = await fetchFamilyProducts(loadedFamilyId);
      if (request !== requestRef.current) return;
      const restoredProduct = findProductBySku(restored, data.sku);
      if (restoredProduct) {
        applyPageData(restoredProduct);
        return;
      }
    }

    try {
      const page = await getFamilyPage(data);
      if (request !== requestRef.current) return;
      applyPageData(page);
      const familyId = page?.familyDetails?.familyId;
      if (familyId && !getCachedFamilyProducts()) fetchFamilyProducts(familyId);
    } catch {
      if (request !== requestRef.current) return;
      hideLoader();
    }
  };

  const applyPageData = (familydetails) => {
    setPageData(familydetails);
    loadedFidRef.current = fid;
    loadImageouchScriptNew();
    const details = familydetails?.familyDetails;
    if (details) {
      setFamilyId(details.familyId);
      if (details.isFamily != false) {
        stockProductRef.current = null;
        setProductStockStatus(null);
      } else if (stockProductRef.current !== String(details.productId ?? 0)) {
        fetchStockStatus(details.productId);
      }
    }
    const crumbs = toBreadcrumb(familydetails?.breadCrumb);
    setBreadcrumb((previous) => crumbs ?? previous);
    setTimeout(hideLoader, 100);
  };

  const getCachedFamilyProducts = () => {
    if (String(familyProductsRef.current.familyId) != String(fid)) return null;
    const products = isValidArray(hybridFamilyProducts) ? hybridFamilyProducts : familyProductsRef.current.products;
    return isValidArray(products) ? products : null;
  };

  const fetchFamilyProducts = async (familyId) => {
    try {
      const products = await getFamilyProducts({
        familyId: familyId,
        userId: String(userId ?? 999),
        priceCode: String(priceCode ?? 3),
      });
      if (!isValidArray(products)) return null;
      const updatedData = products.map((item) => ({ ...item }));
      familyProductsRef.current = { familyId: fid, products: updatedData };
      setHybridFamilyProducts(updatedData);
      return updatedData;
    } catch (error) {
      return null;
    }
  };

  const fetchStockStatus = async (productId) => {
    stockProductRef.current = String(productId ?? 0);
    setProductStockStatus(null);
    const res = await getStockStatus(productId ?? 0);
    if (res && stockProductRef.current === String(productId ?? 0)) setProductStockStatus(res);
  };

  useEffect(() => {
    if (prevValue.current != pageHref) {
      setPageData((prev) => (prev && !prev.isPreview ? { ...prev, reload: true } : prev));
      prevValue.current = pageHref;
      loadedHrefRef.current = pageHref;
      loadInitialData();
      if (!pageOverlay) scrollToTop();
    }
  }, [pageHref, categoryDataAll]);

  useEffect(() => {
    if (!overlaySku) overlayOpenedHereRef.current = false;
  }, [overlaySku]);

  const isFamilyView = pageData?.familyDetails?.isFamily == true && (!skuParam || !!overlaySku);
  const closeOverlay = useCallback(() => {
    if (overlayOpenedHereRef.current) {
      overlayOpenedHereRef.current = false;
      navigate(-1);
    } else {
      navigate(familyHref, { replace: true, state: sameLayerState(location, { [OVERLAY_STATE_KEY]: false }) });
    }
  }, [navigate, familyHref, location]);
  const changeOverlaySku = useCallback(
    (sku) => {
      if (!sku) return;
      const params = new URLSearchParams(familySearch);
      params.set("sku", String(sku).toLowerCase());
      navigate(
        { pathname: location.pathname, search: `?${params.toString()}` },
        { replace: true, state: sameLayerState(location) },
      );
    },
    [navigate, familySearch, location],
  );
  const familyOverlayValue = useMemo(() => {
    if (!isFamilyView) return null;
    return {
      isOverlay: false,
      openProduct: (sku) => {
        if (!sku) return;
        const params = new URLSearchParams(familySearch);
        params.set("sku", String(sku).toLowerCase());
        overlayOpenedHereRef.current = true;
        navigate(
          { pathname: location.pathname, search: `?${params.toString()}` },
          { state: sameLayerState(location, { [OVERLAY_STATE_KEY]: true }) },
        );
      },
    };
  }, [isFamilyView, familySearch, location, navigate]);
  const nestedOverlay = useExitPresence(isFamilyView && overlaySku ? overlaySku : null, OVERLAY_EXIT_MS);

  useEffect(() => {
    if (familyRef.current && familyRef.current != fid) {
      showLoader();
      setPageData((prev) => ({ ...prev, reload: !prev.reload }));
      familyRef.current = fid;
    }
  }, [fid]);

  const getProductHybrid = async (data, request) => {
    try {
      const response = await axiosInstance.post("ProductNew/GetHybridData", data);
      if (request !== requestRef.current) return;
      if (response && response.data) {
        setHybridFilter(response.data);
        setIshybrid(true);
      } else {
        setHybridFilter(null);
        setIshybrid(false);
      }
    } catch {
      if (request !== requestRef.current) return;
      setHybridFilter(null);
      setIshybrid(false);
    }
  };

  return (
    <ProductOverlayContext.Provider value={familyOverlayValue}>
      {pageData && pageData.familyDetails ? (
        <Fragment>
          <HybridBreadCrumb data={breadCrumb} familyDetails={pageData.familyDetails} leading={pageOverlay?.backLink} />
          <ProductDetailsContainer
            params={pageData}
            stock={productStockStatus}
            setIshybrid={setIshybrid}
            hybridData={hybridFilter}
            familyProducts={getCachedFamilyProducts()}
          />
          {pageData.isPreview ? (
            <SuggestionProductSkeleton />
          ) : (
            <Fragment>
              <HtmlFeatureBlogContainer details={pageData.familyDetails} position={"top"} />
              <HybridProductContainer familyDetails={pageData.familyDetails} ishybrid={ishybrid} />
              <HtmlFeatureBlogContainer details={pageData.familyDetails} position={"bottom"} />
              <RelatedProductsContainer params={pageData} />
              <PopularProductContainer params={pageData.familyDetails} />
              {pageData.familyDetails.isFamily == true ? (
                <ProductCardPopularProduct />
              ) : (
                <FrequentlyBoughtTogether params={pageData.familyDetails} />
              )}
            </Fragment>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <BreadcrumbSkeleton leading={pageOverlay?.backLink} />
          <div className="container-fluid container-lg">
            <div className="row">
              <div className="col-lg-12">
                <div className="productsDetail-gallery">
                  <ProductGallerySkeleton />
                  <HybridpageRightLoader />
                </div>
              </div>
            </div>
          </div>
          <SuggestionProductSkeleton />
        </Fragment>
      )}
      {nestedOverlay.value && (
        <ProductOverlay
          sku={nestedOverlay.value}
          fid={fid}
          cachedProduct={findProductBySku(getCachedFamilyProducts(), nestedOverlay.value)}
          familyProducts={getCachedFamilyProducts()}
          breadCrumb={breadCrumb}
          onClose={closeOverlay}
          onSkuChange={changeOverlaySku}
          leaving={nestedOverlay.leaving}
        />
      )}
    </ProductOverlayContext.Provider>
  );
};
